"use client";

import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import { DataTableColumnHeader } from "@/lib/data-table-column-header";
import type { DeliveryNote } from "@/types";
import { formatDateAR } from "@/utils/date";
import { truncateMiddle } from "@/utils/text";

const statusColors: Record<string, string> = {
  uploaded: "bg-blue-500",
  processing: "bg-yellow-500",
  pending_review: "bg-orange-500",
  assigned: "bg-green-500",
  error: "bg-red-500",
};

const statusLabels: Record<string, string> = {
  uploaded: "Cargado",
  processing: "Procesando",
  pending_review: "Pendiente de revisión",
  assigned: "Asignado",
  error: "Error",
};

export const columns: ColumnDef<DeliveryNote>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => {
      const deliveryNote = row.original;
      return <div className="font-mono font-medium">{deliveryNote.id}</div>;
    },
  },
  {
    accessorKey: "number",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Número de Remito" />
    ),
    cell: ({ row }) => {
      const number = row.original.number;
      if (!number) {
        return <div className="text-muted-foreground">-</div>;
      }
      return <div className="font-medium">{number}</div>;
    },
  },
  {
    accessorKey: "purchase_request.sector.name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sector/Obra" />
    ),
    cell: ({ row }) => {
      const purchaseRequest = row.original.purchase_request;
      const sectorName = purchaseRequest?.sector?.name;

      if (!sectorName) {
        return <div className="text-muted-foreground">-</div>;
      }

      const truncatedName = truncateMiddle(sectorName, 20);
      const shouldShowTooltip = sectorName.length > 20;

      if (shouldShowTooltip) {
        return (
          <div className="cursor-help" title={sectorName}>
            {truncatedName}
          </div>
        );
      }

      return <div>{sectorName}</div>;
    },
  },
  {
    accessorKey: "supplier.name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Proveedor" />
    ),
    cell: ({ row }) => {
      const supplier = row.original.supplier;
      const supplierName = supplier?.name;

      if (!supplierName) {
        return <div className="text-muted-foreground">-</div>;
      }

      const truncatedName = truncateMiddle(supplierName, 20);
      const shouldShowTooltip = supplierName.length > 20;

      if (shouldShowTooltip) {
        return (
          <div className="cursor-help" title={supplierName}>
            {truncatedName}
          </div>
        );
      }

      return <div>{supplierName}</div>;
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      const status = row.getValue("status");
      const color = statusColors[status as keyof typeof statusColors] ?? "";
      const label = statusLabels[status as keyof typeof statusLabels] ?? status;
      return <Badge className={color}>{label}</Badge>;
    },
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fecha de Creación" />
    ),
    cell: ({ row }) => {
      const date = row.getValue("created_at");
      return <div>{formatDateAR(date as string | number | Date)}</div>;
    },
  },
];
